import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useGetMatterV2 } from '../../../api/queries/useGetMatterV2';
import { useGetDocFacts } from '@/api/queries/useGetDocFacts';
import { useGetFactDetails } from '@/api/queries/useGetFactDetails';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { processFactHighlights } from '@/new/lib/doc-viewer-utils';
import { cn } from '@/new/lib/utils';

export const FactDocViewer = ({
  docId,
  factId,
  onClose,
  onViewerClick,
  hideFeatureButtons,
  hideCloseButton,
  className,
  onHighlightClick,
  showFactsByDefault = false,
}: {
  docId: string;
  factId?: string | null;
  onClose?: () => void;
  onViewerClick?: () => void;
  hideFeatureButtons?: boolean;
  hideCloseButton?: boolean;
  className?: string;
  onHighlightClick?: (id: string) => void;
  showFactsByDefault?: boolean;
}) => {
  const { matterId, subsetId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);
  const requestSubsetId = subsetId ?? matter?.allDocumentsCaseId ?? undefined;

  const { closeDocViewer } = useDocumentNavigation();

  const [showFacts, setShowFacts] = useState(showFactsByDefault);

  // Data fetching
  const { data: fact } = useGetFactDetails(matterId, requestSubsetId, factId);
  const { data: documentFacts } = useGetDocFacts(matterId, requestSubsetId, docId);

  const { coordinates } = useMemo(() => {
    if (!fact) return { coordinates: [] };
    if (showFacts) {
      if (!documentFacts) return { coordinates: [] };
      return processFactHighlights(documentFacts, docId);
    }
    return processFactHighlights([fact], docId);
  }, [fact, documentFacts, docId, showFacts]);

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="flex-grow overflow-hidden">
        <div className="relative h-full">
          <PDFViewer
            docId={docId}
            highlights={coordinates}
            closeViewer={hideCloseButton ? undefined : (onClose ?? closeDocViewer)}
            highlightedFactId={factId}
            onViewerClick={onViewerClick}
            hideFeatureButtons={hideFeatureButtons}
            showFacts={showFacts}
            setShowFacts={setShowFacts}
            onHighlightClick={onHighlightClick}
          />
        </div>
      </div>
    </div>
  );
};
